import { BuildGroup } from "../../types/build-group";

export const buildEnv = {
  NODE_ENV: process.env.NODE_ENV,
  SERVICE_URL: process.env.SERVICE_URL,
  MAX_LINE_COUNT_PER_LOG: process.env.MAX_LINE_COUNT_PER_LOG,
  DISABLE_KEYBOARD: process.env.DISABLE_KEYBOARD,
  LOCAL_DEVELOPMENT: process.env.LOCAL_DEVELOPMENT,
  DISABLE_ELECTRON_LOGGING: process.env.DISABLE_ELECTRON_LOGGING,
  TRACKING_ENV: process.env.TRACKING_ENV,
  BUILD_GROUP: process.env.BUILD_GROUP,
  CI: process.env.CI
};

export const mergeEnvironment = (buildEnv: NodeJS.ProcessEnv, runtimeEnv: NodeJS.ProcessEnv): NodeJS.ProcessEnv => ({
  ...buildEnv,
  ...runtimeEnv
});

// `process` only exists in Node and Electron; browsers rely on the build-time values alone.
const runtimeEnv: NodeJS.ProcessEnv = globalThis.process?.env ?? {};

// Keep webpack's build-time definitions while allowing runtime values to override them in Node and Electron.
export const env = mergeEnvironment(buildEnv, runtimeEnv);

export interface Envelope {
  _env: any;
  isKeyboardDisabled: boolean;
  isLocalDevelopment: boolean;
  isProductionBuild: boolean;
  isProductionTracking: boolean;
  isElectronLoggingDisabled: boolean;
  isDevMode: boolean;
  isContentToolVerbose: boolean;
  maxLineCountPerLog: number;
  serviceUrl: string;

  /**
   * The service tag is only available via envelope in the Kiosk and Kiosk System Services applications.
   */
  serviceTag: string;

  pwd: string;
  home: string;
  user: string;
  hostname: string;
  puppeteerExecutablePath: string;
  buildGroup: BuildGroup;
  isMobileBuild: boolean;
}

const getServiceUrl = (): string => {
  const serviceUrl = process.env.SERVICE_URL ?? "http://localhost:8001/service/";

  // In browser context, we assume that we have two placeholders which need to be replaced
  if (typeof window !== "undefined") {
    return serviceUrl
      .replace("%hostname%", window.location.host)
      .replace("%serviceTag%", (localStorage.getItem("serviceTag") ?? "").toLowerCase());
  } else {
    return serviceUrl;
  }
};

// We have to enforce some env variables here because
// units tests are not running in the same environment as the application
// and envelope is not mocked in the tests.
const envelope: Envelope = {
  _env: env,
  isKeyboardDisabled: env.DISABLE_KEYBOARD === "true",
  isLocalDevelopment: env.LOCAL_DEVELOPMENT === "true",
  isProductionBuild: env.NODE_ENV === "production",
  isProductionTracking: env.TRACKING_ENV !== "development",
  isElectronLoggingDisabled: env.DISABLE_ELECTRON_LOGGING === "true",
  isDevMode: env.DEV_MODE === "true",
  isContentToolVerbose: env.CONTENT_TOOL_VERBOSE === "true",
  maxLineCountPerLog: parseInt(env.MAX_LINE_COUNT_PER_LOG ?? "", 10) || 10,
  serviceUrl: getServiceUrl(),
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
  serviceTag: env.SERVICETAG!,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
  pwd: env.PWD!,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
  home: env.HOME!,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
  user: env.USER!,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
  hostname: env.HOSTNAME!,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
  puppeteerExecutablePath: env.PUPPETEER_EXECUTABLE_PATH!,
  buildGroup: env.BUILD_GROUP as BuildGroup,
  isMobileBuild: env.BUILD_GROUP === BuildGroup.Mobile || env.BUILD_GROUP === BuildGroup.MobileServices
};

export default envelope;
